/**
 * Partial.ly Announcement Banner + Custom Popup for Shopify
 * Per-tag configuration — the banner shows only on products with matching tags.
 *
 * Installation:
 *   1. Upload this file to your Shopify theme's Assets folder.
 *   2. In layout/theme.liquid, add immediately after the opening <body> tag:
 *
 *        <script>
 *          window.pbTags   = {{ product.tags | json }};
 *          window.pbAmount = {{ product.selected_or_first_available_variant.price | divided_by: 100.0 }};
 *        </script>
 *        <script src="{{ 'partially-banner-www.js' | asset_url }}" defer></script>
 *
 * ─── HOW TO CONFIGURE ────────────────────────────────────────────────────
 *
 * The banner shows on ALL products by default using DEFAULTS.
 * Add an entry to TAG_CONFIG for any tag that needs different settings.
 * Tags are case-insensitive. If a product has multiple matching tags the
 * first match (in order of TAG_CONFIG) wins.
 *
 * Each entry can override any of the DEFAULTS below:
 *   installments    — number of payments shown in the banner, e.g. 3
 *   plans           — payment plan rows shown in the popup
 *   bannerMessage   — override the auto-generated message (optional)
 *   currencySymbol  — override currency symbol, e.g. "$"
 *   cta             — "Learn more" button label
 *
 * Products with none of the listed tags will show the banner using DEFAULTS.
 *
 * ─────────────────────────────────────────────────────────────────────────
 */

(function () {
  "use strict";

  /* ─── DEFAULTS ────────────────────────────────────────────────────────── */
  // Shared settings applied to every product unless overridden per-product.
  const DEFAULTS = {
    currencySymbol: "£",
    installments:   3,
    plans: [
      
      { months: 3,  label: "3 months"  },
    
    ],
    dismissible: false,
    storageKey:  "partially_banner_dismissed",
    animationMs: 360,
  };

  /* ─── TAG CONFIG ─────────────────────────────────────────────────────── */
  // Add one entry per product tag that needs non-default settings.
  // Tags are case-insensitive. Products with no matching tag show the
  // banner using DEFAULTS. Any key not specified falls back to DEFAULTS.
  const TAG_CONFIG = {

    "Dunkirk": {
      installments: 6,
      plans: [
        { months: 6, label: "6 months" },
      ],
    },

    "triumph-vintage": {
      installments: 6,
      plans: [
        { months: 6,  label: "6 months"  },
        
      ],
    },

    // "spread-cost": {
    //   installments: 12,
    //   bannerMessage: "Spread the cost over 12 months with Partial.ly",
    //   plans: [
    //     { months: 12, label: "12 months" },
    //   ],
    // },

  };
  /* ──────────────────────────────────────────────────────────────────────── */

  // Resolved config for the current page — populated in mount().
  let C = Object.assign({}, DEFAULTS);

  /* ─── PRODUCT TAG DETECTION ──────────────────────────────────────────── */
  function getTags() {
    // 1. Passed directly from Liquid (most reliable)
    if (Array.isArray(window.pbTags)) return window.pbTags.map(t => t.toLowerCase());
    // 2. Shopify analytics meta object
    try {
      const meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      if (meta && meta.product && Array.isArray(meta.product.tags)) {
        return meta.product.tags.map(t => t.toLowerCase());
      }
    } catch (_) {}
    return [];
  }

  // Returns the resolved config for the first matching tag, or DEFAULTS if none match.
  // The banner always shows — TAG_CONFIG entries override DEFAULTS per product tag.
  // depositRate is only applied for untagged products (default behaviour).
  function resolveConfig() {
    const productTags = getTags();
    for (const tag of Object.keys(TAG_CONFIG)) {
      if (productTags.includes(tag.toLowerCase())) {
        return Object.assign({}, DEFAULTS, TAG_CONFIG[tag]);
      }
    }
    return Object.assign({}, DEFAULTS);
  }

  /* ─── PRICE DETECTION ─────────────────────────────────────────────────── */
  function getPrice() {
    if (typeof window.pbAmount === "number") return window.pbAmount;
    try {
      const meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      if (meta && meta.product && meta.product.variants && meta.product.variants.length) {
        return meta.product.variants[0].price / 100;
      }
    } catch (_) {}
    const selectors = [
      '[data-product-price]', '.price-item--regular', '.product__price',
      '.price .money', '.ProductPrice', '[itemprop="price"]',
    ];
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el) {
        const val = parseFloat((el.getAttribute("content") || el.textContent).replace(/[^0-9.]/g, ""));
        if (!isNaN(val) && val > 0) return val;
      }
    }
    return null;
  }

  /* ─── HELPERS ─────────────────────────────────────────────────────────── */
  // Rounds up to the nearest cent, matching Partial.ly's installment logic.
  function ceilCent(val) {
    return Math.ceil(val * 100) / 100;
  }

  function fmt(val) {
    return C.currencySymbol + parseFloat(val).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  function buildBannerMessage(price) {
    if (C.bannerMessage) return C.bannerMessage;
    const logoInline = `<span id="pb-msg-logo" style="display:inline-flex;align-items:center;vertical-align:middle;margin-left:6px;line-height:0;">${LOGO_SVG}</span>`;
    if (!price) return `Payment plans available with${logoInline}`;
    if (C.depositRate) {
      const deposit  = ceilCent(price * C.depositRate);
      const monthly  = ceilCent((price - deposit) / (C.installments - 1));
      return `Pay <strong style="font-weight:700;color:#FEFEFE;">${fmt(deposit)}</strong> deposit + 3 payments of <strong style="font-weight:700;color:#FEFEFE;">${fmt(monthly)}</strong> with${logoInline}`;
    }
    const installment = ceilCent(price / C.installments);
    return `Pay in ${C.installments} payments of <strong style="font-weight:700;color:#FEFEFE;">${fmt(installment)}</strong> with${logoInline}`;
  }

  function updateBannerMessage(price) {
    const el = document.getElementById("pb-msg");
    if (!el) return;
    el.innerHTML = buildBannerMessage(price);
  }

  /* ─── SVG ASSETS ──────────────────────────────────────────────────────── */
  const LOGO_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 230 75.22" aria-label="Partial.ly" role="img">
    <path d="M89.47,39.39c-2.06,2.06-4.41,2.32-7.22,2.32H80.52v7.75H75.16V27.37h6.62c3.45,0,6,.2,7.92,2.09a6.54,6.54,0,0,1,1.79,4.73A7,7,0,0,1,89.47,39.39Zm-4.14-7.08c-.63-.63-1.53-.7-3.08-.7H80.52v5.8h1.56c2,0,2.75-.27,3.21-.73a3.18,3.18,0,0,0,.77-2.22A3.1,3.1,0,0,0,85.33,32.31Z" fill="#fff"/>
    <path d="M102.58,49.46V48.07A7.21,7.21,0,0,1,98,49.83a5,5,0,0,1-5.33-5.37c0-6.06,7.31-6.06,9-6.06H102a1.88,1.88,0,0,0-.4-1.49,2.61,2.61,0,0,0-2-.53,11.05,11.05,0,0,0-4.57,1.13l-1.59-3.15a11,11,0,0,1,6.42-1.79c2.52,0,4.41.63,5.7,1.92S107,37.84,107,39.86v9.6Zm-.49-7.68c-1.26,0-3.58-.07-4.54.89A2,2,0,0,0,99,46.05a5.34,5.34,0,0,0,3-.93Z" fill="#fff"/>
    <path d="M121.27,37a4.43,4.43,0,0,0-1-.13,5.21,5.21,0,0,0-4.4,2.25V49.46h-5.3V33h4.37v2.18a7.29,7.29,0,0,1,5.5-2.68,3.77,3.77,0,0,1,2,.5Z" fill="#fff"/>
    <path d="M134,50a6.44,6.44,0,0,1-4.53-1.59c-1.49-1.49-1.49-3.15-1.49-5.63V37.18h-2.85V33h2.85V28.5h5.29V33h4.48v4.14h-4.48V43.1c0,1.13.07,2.59,1.93,2.59a4.94,4.94,0,0,0,1.95-.37l1.32,3.48A8.39,8.39,0,0,1,134,50Z" fill="#fff"/>
    <path d="M144.19,31.21a2.8,2.8,0,1,1,0-5.59,2.8,2.8,0,0,1,0,5.59Zm-2.68,18.25V33h5.3V49.46Z" fill="#fff"/>
    <path d="M159.92,49.46V48.07a7.21,7.21,0,0,1-4.63,1.76A5,5,0,0,1,150,44.46c0-6.06,7.32-6.06,9-6.06h.37a1.88,1.88,0,0,0-.4-1.49,2.61,2.61,0,0,0-2-.53,11.05,11.05,0,0,0-4.57,1.13l-1.59-3.15a11,11,0,0,1,6.42-1.79c2.52,0,4.41.63,5.7,1.92s1.32,3.35,1.32,5.37v9.6Zm-.49-7.68c-1.26,0-3.58-.07-4.54.89a2,2,0,0,0,1.49,3.38,5.34,5.34,0,0,0,3.05-.93Z" fill="#fff"/>
    <path d="M167.91,49.46v-24h5.3v24Z" fill="#fff"/>
    <path d="M180.16,49.73a3.07,3.07,0,1,1,0-6.13,3.07,3.07,0,0,1,0,6.13Z" fill="#fff"/>
    <path d="M187.08,49.46v-24h5.3v24Z" fill="#fff"/>
    <path d="M207.09,49.3c-1.82,4.57-2.42,5.33-3.28,6.19-.6.6-1.95,1.76-5.07,1.76a8.26,8.26,0,0,1-4.47-1.2l1.59-3.64a6.54,6.54,0,0,0,2.62.63,2.7,2.7,0,0,0,2.09-.8A8.57,8.57,0,0,0,202,49.4L195,33h5.63l4,11,3.77-11h5.23Z" fill="#fff"/>
    <path d="M40.31,58.77a18.42,18.42,0,0,1-18.4-18.4V38.53H38.47V22h1.84a18.4,18.4,0,0,1,0,36.8ZM34.79,34.85H16.39V33A16.58,16.58,0,0,1,33,16.45h1.84ZM20.2,31.17H31.11V20.26A12.91,12.91,0,0,0,20.2,31.17Zm22,11V55A14.76,14.76,0,0,0,54.92,42.21Zm-16.45,0A14.75,14.75,0,0,0,38.47,55V42.21Zm16.45-3.68H54.92A14.77,14.77,0,0,0,42.15,25.76Z" fill="#fff" fill-rule="evenodd"/>
  </svg>`;

  const WAVE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 668" preserveAspectRatio="none" aria-hidden="true">
    <path d="M0,0 L1440,0 L1440,694.204692 C1169.68408,801.931769 925.733733,801.931769 708.148973,694.204692 C490.564212,586.477616 254.514555,586.477616 0,694.204692 L0,0 Z"
      fill="rgba(255,255,255,0.07)" transform="translate(720,387.5) scale(-1,1) translate(-720,-387.5)"/>
  </svg>`;

  const CLOSE_ICON = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
    <line x1="2" y1="2" x2="12" y2="12"/><line x1="12" y1="2" x2="2" y2="12"/>
  </svg>`;

  const CHECK_ICON = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#32B6DB" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <polyline points="2,7 5.5,10.5 12,3.5"/>
  </svg>`;

  /* ─── STYLES ──────────────────────────────────────────────────────────── */
  const CSS = `
    #pb-banner *, #pb-banner *::before, #pb-banner *::after,
    #pb-popup  *, #pb-popup *::before,  #pb-popup *::after  {
      box-sizing: border-box; margin: 0; padding: 0;
    }
    #pb-banner {
      position: relative; width: 100%; height: 54px;
      background: linear-gradient(45deg, #574FE5 0%, #32B6DB 100%);
      display: flex; align-items: center; justify-content: center;
      overflow: hidden; z-index: 1000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
      transition: max-height ${DEFAULTS.animationMs}ms cubic-bezier(.4,0,.2,1),
                  opacity   ${DEFAULTS.animationMs}ms ease;
      max-height: 54px; opacity: 1;
    }
    #pb-banner.pb-out { max-height: 0 !important; opacity: 0; pointer-events: none; }
    #pb-wave  { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; }
    #pb-inner { position: relative; z-index: 1; display: flex; align-items: center; gap: 16px; }
    #pb-msg-logo svg { height: 44px; width: auto; }
    #pb-msg, #pb-msg strong { color: #FEFEFE !important; }
    #pb-msg   { font-size: 13.5px; font-weight: 500; white-space: nowrap; }
    #pb-cta {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 6px 15px; border-radius: 100px;
      border: 1.5px solid rgba(255,255,255,0.55);
      background: rgba(255,255,255,0.15);
      font-size: 11.5px; font-weight: 700; letter-spacing: 0.5px;
      text-transform: uppercase; color: #FEFEFE !important;
      cursor: pointer; white-space: nowrap; flex-shrink: 0;
      font-family: inherit; transition: background 0.18s, transform 0.15s;
    }
    #pb-cta:hover { background: rgba(255,255,255,0.28); transform: scale(1.03); }
    #pb-dismiss {
      position: absolute; right: 16px; top: 50%; transform: translateY(-50%);
      z-index: 2; background: none; border: none; cursor: pointer;
      padding: 5px; display: flex; align-items: center;
      color: white; opacity: 0.65; transition: opacity 0.15s, transform 0.15s; border-radius: 50%;
    }
    #pb-dismiss:hover { opacity: 1; transform: translateY(-50%) scale(1.18); }
    @media (max-width: 580px) { #pb-msg { font-size: 11px; } }

    #pb-popup {
      display: none; position: fixed; inset: 0; z-index: 99999;
      align-items: center; justify-content: center; padding: 16px;
      background: rgba(30,28,60,0.5);
      backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
    }
    #pb-popup.pb-open { display: flex; }
    #pb-card {
      position: relative; width: 100%; max-width: 400px;
      background: #fff; border-radius: 18px; overflow: hidden;
      box-shadow: 0 24px 64px rgba(87,79,229,0.18), 0 4px 20px rgba(0,0,0,0.10);
      animation: pb-in ${DEFAULTS.animationMs}ms cubic-bezier(.34,1.46,.64,1) both;
    }
    @keyframes pb-in {
      from { opacity: 0; transform: scale(0.93) translateY(14px); }
      to   { opacity: 1; transform: scale(1) translateY(0); }
    }
    #pb-card-header {
      background: linear-gradient(45deg, #574FE5 0%, #32B6DB 100%);
      padding: 22px 22px 20px; position: relative; overflow: hidden;
    }
    #pb-card-header-wave { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; }
    #pb-header-logo { position: relative; z-index: 1; line-height: 0; }
    #pb-header-logo svg { height: 26px; width: auto; }
    #pb-header-sub {
      position: relative; z-index: 1; margin-top: 7px;
      font-size: 13px; color: rgba(255,255,255,0.80); font-weight: 400; line-height: 1.4;
    }
    #pb-close {
      position: absolute; top: 14px; right: 14px; z-index: 10;
      width: 28px; height: 28px; border-radius: 50%; border: none; cursor: pointer;
      background: rgba(255,255,255,0.18); color: white;
      display: flex; align-items: center; justify-content: center; transition: background 0.15s;
    }
    #pb-close:hover { background: rgba(255,255,255,0.32); }
    #pb-card-body { padding: 20px; }
    #pb-amount-row {
      display: flex; align-items: baseline; justify-content: space-between;
      margin-bottom: 16px; padding-bottom: 16px; border-bottom: 1px solid #EBEBF5;
    }
    #pb-amount-label { font-size: 12px; color: #808393; font-weight: 500; text-transform: uppercase; letter-spacing: 0.4px; }
    #pb-amount-value { font-size: 22px; font-weight: 700; color: #4D5061; }
    #pb-plans { display: flex; flex-direction: column; gap: 10px; margin-bottom: 18px; }
    .pb-plan {
      display: flex; align-items: center; justify-content: space-between;
      padding: 12px 14px; border-radius: 10px;
      border: 1.5px solid #EBEBF5; background: #FAFAFA;
      transition: border-color 0.15s, background 0.15s;
    }
    .pb-plan:hover { border-color: #574FE5; background: #F5F4FF; }
    .pb-plan-left { display: flex; align-items: center; gap: 10px; }
    .pb-plan-check { flex-shrink: 0; }
    .pb-plan-label { font-size: 13.5px; font-weight: 600; color: #4D5061; }
    .pb-plan-sub   { font-size: 11.5px; color: #808393; margin-top: 1px; }
    .pb-plan-right { text-align: right; }
    .pb-plan-amount { font-size: 15px; font-weight: 700; color: #574FE5; }
    .pb-plan-total  { font-size: 11px; color: #808393; margin-top: 1px; }
    #pb-footer-note { text-align: center; margin-top: 12px; font-size: 11px; color: #ADADBD; line-height: 1.4; }
  `;

  /* ─── BUILD BANNER ────────────────────────────────────────────────────── */
  function buildBanner(price) {
    const style = document.createElement("style");
    style.textContent = CSS;
    document.head.appendChild(style);

    const banner = document.createElement("div");
    banner.id = "pb-banner";
    banner.setAttribute("role", "banner");

    const wave = document.createElement("div");
    wave.id = "pb-wave";
    wave.innerHTML = WAVE_SVG;
    banner.appendChild(wave);

    const inner = document.createElement("div");
    inner.id = "pb-inner";

    const msg = document.createElement("span");
    msg.id = "pb-msg";
    msg.innerHTML = buildBannerMessage(price);

    inner.appendChild(msg);
    banner.appendChild(inner);

    return banner;
  }

  /* ─── BUILD POPUP ─────────────────────────────────────────────────────── */
  function buildPopup(price) {
    const backdrop = document.createElement("div");
    backdrop.id = "pb-popup";
    backdrop.setAttribute("role", "dialog");
    backdrop.setAttribute("aria-modal", "true");
    backdrop.setAttribute("aria-label", "Payment plan options");
    backdrop.addEventListener("click", e => { if (e.target === backdrop) closePopup(); });

    const card = document.createElement("div");
    card.id = "pb-card";

    const header = document.createElement("div");
    header.id = "pb-card-header";

    const headerWave = document.createElement("div");
    headerWave.id = "pb-card-header-wave";
    headerWave.innerHTML = WAVE_SVG;

    const headerLogo = document.createElement("div");
    headerLogo.id = "pb-header-logo";
    headerLogo.innerHTML = LOGO_SVG;

    const headerSub = document.createElement("p");
    headerSub.id = "pb-header-sub";
    headerSub.textContent = "Split your purchase into easy monthly payments.";

    const closeBtn = document.createElement("button");
    closeBtn.id = "pb-close";
    closeBtn.setAttribute("aria-label", "Close");
    closeBtn.innerHTML = CLOSE_ICON;
    closeBtn.addEventListener("click", closePopup);

    header.appendChild(headerWave);
    header.appendChild(headerLogo);
    header.appendChild(headerSub);
    header.appendChild(closeBtn);

    const body = document.createElement("div");
    body.id = "pb-card-body";

    const amountRow = document.createElement("div");
    amountRow.id = "pb-amount-row";
    amountRow.innerHTML = `
      <span id="pb-amount-label">Order total</span>
      <span id="pb-amount-value">${price ? fmt(price) : "—"}</span>`;
    body.appendChild(amountRow);

    const plansEl = document.createElement("div");
    plansEl.id = "pb-plans";

    C.plans.forEach(plan => {
      const row = document.createElement("div");
      row.className = "pb-plan";
      const perMonth = price ? fmt(price / plan.months) : "—";
      const total    = price ? fmt(price) : "—";
      row.innerHTML = `
        <div class="pb-plan-left">
          <div class="pb-plan-check">${CHECK_ICON}</div>
          <div>
            <div class="pb-plan-label">${plan.label}</div>
            <div class="pb-plan-sub">Pay monthly, no hidden fees</div>
          </div>
        </div>
        <div class="pb-plan-right">
          <div class="pb-plan-amount">${perMonth}<span style="font-size:11px;font-weight:500;color:#808393;">/mo</span></div>
          <div class="pb-plan-total">Total ${total}</div>
        </div>`;
      plansEl.appendChild(row);
    });
    body.appendChild(plansEl);

    const footer = document.createElement("p");
    footer.id = "pb-footer-note";
    footer.textContent = "Flexible payment plans powered by Partial.ly. Subject to eligibility.";
    body.appendChild(footer);

    card.appendChild(header);
    card.appendChild(body);
    backdrop.appendChild(card);
    return backdrop;
  }

  /* ─── VARIANT CHANGE LISTENER ─────────────────────────────────────────── */
  function watchVariantChanges() {
    document.addEventListener("variant:changed", function (e) {
      const price = e.detail && e.detail.variant && e.detail.variant.price;
      if (price) updateBannerMessage(price / 100);
    });
    document.addEventListener("change", function (e) {
      if (!e.target || !e.target.closest('form[action*="/cart/add"]')) return;
      setTimeout(function () {
        const fresh = getPrice();
        if (fresh) updateBannerMessage(fresh);
      }, 150);
    });
  }

  /* ─── OPEN / CLOSE POPUP ──────────────────────────────────────────────── */
  let popupBuilt = false;

  function openPopup() {
    if (!popupBuilt) {
      document.body.appendChild(buildPopup(getPrice()));
      popupBuilt = true;
    }
    document.getElementById("pb-popup").classList.add("pb-open");
    document.body.style.overflow = "hidden";
  }

  function closePopup() {
    const el = document.getElementById("pb-popup");
    if (el) el.classList.remove("pb-open");
    document.body.style.overflow = "";
  }

  document.addEventListener("keydown", e => { if (e.key === "Escape") closePopup(); });

  /* ─── DISMISS BANNER ──────────────────────────────────────────────────── */
  function dismissBanner() {
    const b = document.getElementById("pb-banner");
    if (!b) return;
    b.classList.add("pb-out");
    setTimeout(() => b.remove(), C.animationMs + 60);
    if (C.dismissible) localStorage.setItem(C.storageKey, "1");
  }

  /* ─── MOUNT ───────────────────────────────────────────────────────────── */
  function mount() {
    // Resolve config for this product — bail if not in PRODUCT_CONFIG
    const resolved = resolveConfig();
    if (!resolved) return;
    C = resolved;

    if (C.dismissible && localStorage.getItem(C.storageKey) === "1") return;

    const price = getPrice();

    // On the homepage, inject the banner above both collections sections.
    const collectionSections = document.querySelectorAll(
      "#TabCollections--template--26858557374849__section_tab_collections_P9JGw8, #TabCollections--template--26858557374849__section_tab_collections_T7JxPz"
    );
    if (collectionSections.length) {
      collectionSections.forEach(section => {
        section.parentNode.insertBefore(buildBanner(price), section);
      });
      watchVariantChanges();
      return;
    }

    // On collection pages, insert above the collection nav.
    const collectionNav = document.querySelector("[data-collection-nav]");
    if (collectionNav && collectionNav.parentNode) {
      collectionNav.parentNode.insertBefore(buildBanner(price), collectionNav);
      watchVariantChanges();
      return;
    }

    // On all other pages, insert below the header.
    const header = document.querySelector(".header-sections");
    if (header && header.parentNode) {
      header.parentNode.insertBefore(buildBanner(price), header.nextSibling);
    } else {
      document.body.insertBefore(buildBanner(price), document.body.firstChild);
    }
    watchVariantChanges();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", mount);
  } else {
    mount();
  }
})();